import { z } from 'zod';

/**
 * THE PAYMENT GATEWAY CONTRACT — what this product needs from Razorpay, and nothing else.
 *
 * Defined here, in shared, for the same reason the Relay adapter is: the gateway's exact
 * response shapes are a vendor's business and they change, so the product talks to THIS
 * interface and one file implements it. A fake implements the same interface and is what runs
 * in development and in every test, which is the only way to be sure no test can ever move real
 * money.
 *
 * The vocabulary is deliberately ours, not Razorpay's. `mandateMethod` rather than
 * `payment_method`; `gatewaySubscriptionId` rather than `sub_id`. A vendor's field names
 * leaking into the domain is how a codebase ends up unable to change provider without touching
 * two hundred files.
 */

/**
 * How the shop authorises recurring collection.
 *
 * UPI Autopay first, because that is what this market actually uses — a small boutique owner in
 * Coimbatore has a UPI app and may well not have a card that supports recurring mandates at all.
 * Card and netbanking are the fallbacks, and `manual` is the one that matters most: a shop that
 * cannot or will not set up any mandate is still a paying customer, and refusing their money
 * because they would not hand over a standing instruction would be absurd.
 */
export const MANDATE_METHODS = ['upi', 'card', 'netbanking', 'manual'] as const;
export type MandateMethod = (typeof MANDATE_METHODS)[number];
export const mandateMethodSchema = z.enum(MANDATE_METHODS);

/** What the gateway says a subscription is doing. Mapped onto our own statuses by the service. */
export const GATEWAY_SUBSCRIPTION_STATES = [
  'created',
  'authenticated',
  'active',
  'pending',
  'halted',
  'cancelled',
  'completed',
] as const;
export type GatewaySubscriptionState = (typeof GATEWAY_SUBSCRIPTION_STATES)[number];

export interface GatewaySubscription {
  readonly id: string;
  readonly state: GatewaySubscriptionState;
  readonly planId: string;
  readonly customerId: string;
  readonly mandateMethod: MandateMethod;
  /** Where to send the owner to authorise the mandate, or to pay a one-off. */
  readonly authorisationUrl: string | null;
  readonly currentPeriodStart: Date | null;
  readonly currentPeriodEnd: Date | null;
  readonly amountPaise: number;
}

export interface GatewayPayment {
  readonly id: string;
  readonly subscriptionId: string | null;
  readonly amountPaise: number;
  readonly status: 'captured' | 'failed' | 'refunded';
  readonly method: MandateMethod;
  readonly at: Date;
  /** The gateway's own failure text, shown to the owner because it is often actionable. */
  readonly failureReason: string | null;
}

export interface CreateSubscriptionCommand {
  /** Our own plan and cycle, which the implementation maps to the gateway's plan id. */
  readonly planKey: string;
  readonly billingCycle: 'monthly' | 'annual';
  readonly amountPaise: number;
  readonly mandateMethod: MandateMethod;
  readonly shopName: string;
  /** Identity, and what the gateway emails a receipt to. */
  readonly ownerEmail: string;
  /** Contact metadata; absent when the shop never recorded a number. */
  readonly ownerPhone: string | null;
  /** Ours, echoed back on every webhook, so a callback can find the shop without a lookup table. */
  readonly reference: string;
}

export interface CreatePaymentLinkCommand {
  readonly amountPaise: number;
  readonly description: string;
  readonly shopName: string;
  readonly ownerEmail: string;
  readonly ownerPhone: string | null;
  readonly reference: string;
}

export interface GatewayPaymentLink {
  readonly id: string;
  readonly url: string;
  readonly amountPaise: number;
  readonly expiresAt: Date | null;
}

/** The webhook events this product acts on. Anything else is acknowledged and ignored. */
export const GATEWAY_WEBHOOK_EVENTS = [
  'subscription.activated',
  'subscription.charged',
  'subscription.pending',
  'subscription.halted',
  'subscription.cancelled',
  'payment.failed',
  'payment.captured',
] as const;
export type GatewayWebhookEvent = (typeof GATEWAY_WEBHOOK_EVENTS)[number];

export interface GatewayWebhookNotification {
  /** The gateway's own event id. THE idempotency key — see `webhook-event.model.ts`. */
  readonly eventId: string;
  readonly event: GatewayWebhookEvent;
  readonly at: Date;
  readonly subscriptionId: string | null;
  readonly paymentId: string | null;
  /** Our `reference` from the create call, when the payload carries it back. */
  readonly reference: string | null;
  readonly amountPaise: number | null;
  readonly currentPeriodEnd: Date | null;
  readonly failureReason: string | null;
}

export interface ReadWebhookInput {
  /** The bytes AS SENT. Re-serialised JSON would compare our formatting to theirs. */
  readonly rawBody: Buffer;
  readonly headers: Record<string, string | undefined>;
  readonly secret: string | null;
}

/** Thrown when a callback cannot be proved to have come from the gateway. */
export class GatewaySignatureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GatewaySignatureError';
  }
}

/** Thrown when the gateway refuses a request. Carries its own words, which are often useful. */
export class GatewayRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'GatewayRequestError';
  }
}

export interface PaymentGatewayClient {
  createSubscription(command: CreateSubscriptionCommand): Promise<GatewaySubscription>;
  /** The backstop for a shop with no mandate: one link, one payment, one period. */
  createPaymentLink(command: CreatePaymentLinkCommand): Promise<GatewayPaymentLink>;
  cancelSubscription(subscriptionId: string, options: { immediately: boolean }): Promise<void>;
  /** What the gateway believes right now — the reconciliation job's only question. */
  fetchSubscription(subscriptionId: string): Promise<GatewaySubscription | null>;
  fetchPayments(subscriptionId: string, since: Date): Promise<GatewayPayment[]>;
  /** Verifies the signature and normalises the payload. Throws `GatewaySignatureError`. */
  readWebhook(input: ReadWebhookInput): GatewayWebhookNotification | null;
}

/**
 * How a gateway state maps onto the dunning event it should raise.
 *
 * A table rather than a switch inside the handler, so the mapping is visible beside the machine
 * it drives. `subscription.pending` is Razorpay's word for "we tried and it did not go through
 * and we will try again", which is exactly a failed payment from the shop's point of view.
 */
export const WEBHOOK_TO_DUNNING_EVENT: Readonly<
  Record<
    GatewayWebhookEvent,
    'subscription_activated' | 'payment_succeeded' | 'payment_failed' | 'cancelled' | null
  >
> = Object.freeze({
  'subscription.activated': 'subscription_activated',
  'subscription.charged': 'payment_succeeded',
  'subscription.pending': 'payment_failed',
  'subscription.halted': 'payment_failed',
  'subscription.cancelled': 'cancelled',
  'payment.failed': 'payment_failed',
  // A one-off capture is what the manual payment link produces, and it pays for a period.
  'payment.captured': 'payment_succeeded',
});
